import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { User } from "../../types/User";
import api from "../../utils/api";

interface PaginatedUserResponse {
  data: User[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
}

interface AdminState {
  isAuth: boolean;
  isLoading: boolean;
  unverifiedUsers: User[];
  verifiedUsers: User[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
  error?: string;
}

const initialState: AdminState = {
  isAuth: false,
  isLoading: false,
  unverifiedUsers: [],
  verifiedUsers: [],
  totalCount: 0,
  currentPage: 1,
  totalPages: 1,
  error: undefined,
};

export const getUnverifiedUsers = createAsyncThunk<
  PaginatedUserResponse,
  { page?: number; limit?: number; search?: string }
>("admin/getUnverifiedUsers", async ({ page = 1, limit = 50, search = "" }) => {
  const response = await api.get(
    `/auth/admin/new-users`,
    { params: { page, limit, search } }
  );
  return response.data.data;
});

export const verifyUsers = createAsyncThunk<
  {
    results: {
      user_id: number;
      verified?: boolean;
      verifySmile?: boolean;
      status: string;
    }[];
  },
  {
    updateData: {
      user_id: number;
      verified?: boolean;
      verifySmile?: boolean;
    }[];
  }
>("admin/verifyUsers", async ({ updateData }) => {
  const response = await api.put(
    `/auth/admin/verify-user`,
    { updateData },
    {
      headers: {
        "Content-Type": "application/json",
      },
    }
  );
  return response.data.data;
});

const AdminRequestControlsSlice = createSlice({
  name: "adminRequestControls",
  initialState,
  reducers: {
    resetUnverifiedUsersState: (state) => {
      state.unverifiedUsers = [];
      state.totalCount = 0;
      state.currentPage = 1;
      state.totalPages = 1;
      state.isLoading = false;
      state.error = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getUnverifiedUsers.pending, (state) => {
        state.isLoading = true;
        state.error = undefined;
      })
      .addCase(getUnverifiedUsers.fulfilled, (state, action: PayloadAction<PaginatedUserResponse>) => {
        state.isLoading = false;
        state.unverifiedUsers = action.payload.data;
        state.totalCount = action.payload.totalCount;
        state.currentPage = action.payload.currentPage;
        state.totalPages = action.payload.totalPages;
      })
      .addCase(getUnverifiedUsers.rejected, (state, action) => {
        state.isLoading = false;
        state.unverifiedUsers = [];
        state.error = action.error.message;
      })

      .addCase(verifyUsers.pending, (state) => {
        state.isLoading = true;
        state.error = undefined;
      })
      .addCase(verifyUsers.fulfilled, (state, action) => {
        state.isLoading = false;

        // Once a user is granted access to any portal, they drop out of the
        // requests list.
        for (const result of action.payload.results) {
          const { user_id, verified, verifySmile } = result;

          const granted = verified === true || verifySmile === true;
          if (!granted) continue;

          const existed = state.unverifiedUsers.some(
            (u) => u.user_id === user_id
          );

          state.unverifiedUsers = state.unverifiedUsers.filter(
            (u) => u.user_id !== user_id
          );

          if (existed && state.totalCount > 0) state.totalCount -= 1;
        }
      })
      .addCase(verifyUsers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      });
  },
});

export const { resetUnverifiedUsersState } = AdminRequestControlsSlice.actions;
export default AdminRequestControlsSlice.reducer;